import { reactRouterV6BrowserTracingIntegration, type BrowserOptions, replayIntegration, init } from '@sentry/react';
import React from 'react';
import { useLocation, useNavigationType, createRoutesFromChildren, matchRoutes } from 'react-router-dom';
import runtime from './runtime';

const { sentry: sentryConfig } = runtime;

const integrations = [
    reactRouterV6BrowserTracingIntegration({
        enableInp: true,
        useEffect: React.useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
    }),
];

const sentryInitOptions: BrowserOptions = {
    release: sentryConfig.release,
    dsn: sentryConfig.dsn,
    environment: sentryConfig.environment,
    ignoreErrors: [
        // IAB / Ad Tech / Consent
        /__iabjs/i,
        /IABTranslation/i,
        /UserMessageHandlersNamespace/i,
        /__tcfapi/i,
        /iab_unified_bridge/i,

        // Safari WebKit
        /hxp-modal-detection/i,
        /PageTranslationRequiredListener/i,
        /webkit\.messageHandlers/i,

        // Third-party Performance API issues
        /Cannot read property.*domInteractive/i,
        /Cannot read property.*performance\.timing/i,
        /undefined.*reading.*domInteractive/i,

        // Browser detection scripts (NEW)
        /xbrowser is not defined/i,
        /swbrowser is not defined/i,
        /ReferenceError.*browser.*not defined/i,

        // Other common third-party noise
        /ResizeObserver loop/i,
        /Non-Error promise rejection/i,
    ],

    beforeSend(event) {
        const exception = event.exception?.values?.[0]?.value || '';
        const message = event.message || '';

        // Comprehensive third-party noise filter
        const noisePatterns = [
            '__iabjs',
            'UserMessageHandlersNamespace',
            'domInteractive',
            'hxp-modal-detection',
            'IABTranslation',
            'xbrowser',
            'swbrowser',
        ];

        if (noisePatterns.some(pattern => exception.includes(pattern) || message.includes(pattern))) {
            return null;
        }

        return event;
    },

    denyUrls: [
        // Porsche CDN errors
        /cdn\.ui\.porsche\.com/i,

        // Other denylisted URLs
        /^safari-extension:\/\//i,
        /^moz-extension:\/\//i,
        /^chrome-extension:\/\//i,
        /googletagmanager\.com/i,
    ],
};

if (sentryConfig.tracing) {
    sentryInitOptions.tracesSampleRate = sentryConfig.tracesSampleRate;
}

if (sentryConfig.profiling) {
    sentryInitOptions.tracesSampleRate = sentryConfig.profilingSampleRate;
    sentryInitOptions.profilesSampleRate = sentryConfig.profilingSampleRate;
}

if (sentryConfig.replay) {
    integrations.push(
        replayIntegration({
            networkDetailAllowUrls: [window.location.origin],
            maskAllInputs: true,
            maskAllText: false,
        })
    );

    sentryInitOptions.replaysSessionSampleRate = sentryConfig.replaySampleRate;
    sentryInitOptions.replaysOnErrorSampleRate = 1.0;
}

sentryInitOptions.integrations = integrations;

init(sentryInitOptions);
